import type { SystemMapStopDetail, UrbanNetworkEdge } from './api';
import type { StopOverride } from './systemMapReview';

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="smap-detail-row">
      <span className="smap-detail-row-label">{label}</span>
      <span className="smap-detail-row-value">{value}</span>
    </div>
  );
}

const SIN_DATO = 'Sin dato';

function fmtBool(v: boolean | null): string {
  if (v === null) return SIN_DATO;
  return v ? 'Sí' : 'No';
}

function fmtNum(v: number | null, decimals = 1): string {
  return v === null ? SIN_DATO : v.toFixed(decimals);
}

function fmtStr(v: string | null): string {
  return v && v.length > 0 ? v : SIN_DATO;
}

const STATUS_LABELS: Record<string, string> = {
  valid: 'Válida',
  needs_review: 'Requiere revisión',
  orphaned: 'Huérfana (sin asociación válida)',
};

function statusLabel(status: string | null): string {
  if (!status) return SIN_DATO;
  return STATUS_LABELS[status] ?? status;
}

/** Identificación + servicio declarado — nunca insinúa que la parada es inválida por falta de servicio operativo vigente. */
function IdentificationSection({ detail }: { detail: SystemMapStopDetail }): JSX.Element {
  return (
    <>
      <div className="smap-detail-section">
        <p className="smap-detail-section-title">Identificación</p>
        <Row label="ID municipal" value={fmtStr(detail.municipalStopId)} />
        <Row label="ID interno" value={detail.id} />
        <Row label="Nombre / dirección" value={fmtStr(detail.name)} />
        <Row label="Código" value={fmtStr(detail.code)} />
        <Row label="Tipo" value={fmtStr(detail.type)} />
        <Row
          label="Coordenadas"
          value={detail.lat !== null && detail.lon !== null ? `${detail.lat.toFixed(6)}, ${detail.lon.toFixed(6)}` : SIN_DATO}
        />
        <Row label="Accesible" value={fmtBool(detail.accessible)} />
        <Row label="Activa" value={fmtBool(detail.isActive)} />
      </div>

      <div className="smap-detail-section">
        <p className="smap-detail-section-title">Servicio municipal declarado</p>
        {detail.declaredRoutes && detail.declaredRoutes.length > 0 ? (
          <Row label="Líneas declaradas" value={detail.declaredRoutes.join(', ')} />
        ) : (
          <p className="smap-detail-empty">Sin servicio operativo asociado</p>
        )}
      </div>
    </>
  );
}

/**
 * "Editar asociación" está disponible para CUALQUIER parada, sin importar
 * su estado real (incluso `valid`/confidence alta) — la inspección visual
 * manual puede detectar falsos positivos que el QA automático no marcó.
 * Nunca es un toggle global: cada parada se edita por separado, por
 * acción explícita.
 */
function AssociationSection({
  detail,
  editing,
  onToggleEdit,
}: {
  detail: SystemMapStopDetail;
  editing: boolean;
  onToggleEdit: () => void;
}): JSX.Element {
  return (
    <div className="smap-detail-section">
      <div className="smap-detail-header" style={{ marginBottom: '6px' }}>
        <p className="smap-detail-section-title" style={{ margin: 0 }}>
          Asociación espacial actual
        </p>
        <button type="button" className="smap-edit-toggle" onClick={onToggleEdit}>
          {editing ? 'Cerrar edición' : 'Editar asociación'}
        </button>
      </div>
      <Row label="Canonical edge" value={fmtStr(detail.edgeId)} />
      <Row label="Lado del edge" value={fmtStr(detail.sideOfEdge)} />
      <Row label="Posición en el edge" value={fmtNum(detail.positionOnEdge, 2)} />
      <Row label="Distancia al edge" value={detail.distanceToNetworkM !== null ? `${fmtNum(detail.distanceToNetworkM, 1)} m` : SIN_DATO} />
      <Row label="Confidence" value={detail.confidenceScore !== null ? fmtNum(detail.confidenceScore, 2) : SIN_DATO} />
      <Row label="Dentro de umbral" value={fmtBool(detail.withinThreshold)} />
      <Row label="Tipo de proyección" value={fmtStr(detail.projectionType)} />
      <Row label="Método" value={fmtStr(detail.associationMethod)} />
    </div>
  );
}

/** Banner de modo edición — aparece SOLO cuando el usuario lo activó explícitamente para esta parada. Guardar queda deshabilitado con mensaje visible; Cancelar no deja ningún rastro (limpia candidato + sale del modo). */
function EditModeBlock({
  hasCandidate,
  onCancel,
}: {
  hasCandidate: boolean;
  onCancel: () => void;
}): JSX.Element {
  return (
    <div className="smap-detail-section smap-persistence-block">
      <p className="smap-detail-section-title">Modo edición</p>
      <p className="smap-detail-empty">
        {hasCandidate
          ? 'Candidato seleccionado abajo — comparalo con la asociación actual antes de decidir.'
          : 'Activá "Red urbana" y clickeá un segmento para proponer un candidato.'}
      </p>
      <button type="button" className="smap-save-disabled" disabled>
        Guardar asociación
      </button>
      <p className="smap-persistence-note">Guardado deshabilitado — pendiente de habilitar persistencia segura.</p>
      <button type="button" className="smap-reset-btn" onClick={onCancel}>
        Cancelar
      </button>
    </div>
  );
}

/** Badge de "confirmado en esta sesión" — nunca se mezcla con el estado real, siempre rotulado como simulación no persistida. */
function SessionOverrideBadge({ override }: { override: StopOverride }): JSX.Element {
  return (
    <div className="smap-detail-section smap-session-badge">
      <p className="smap-detail-empty">
        ✓ Confirmada en esta sesión (estado real al confirmar: {statusLabel(override.confirmedRealStatus)}) — no
        persistido, se pierde al recargar la página.
      </p>
    </div>
  );
}

function QaSection({ detail }: { detail: SystemMapStopDetail }): JSX.Element {
  const hasReviewEvidence = detail.reviewRequiredAt || detail.reviewSource || detail.reviewReason;
  return (
    <div className="smap-detail-section">
      <p className="smap-detail-section-title">QA</p>
      <Row label="Estado" value={statusLabel(detail.status)} />
      {hasReviewEvidence ? (
        <>
          <Row label="Revisión requerida desde" value={fmtStr(detail.reviewRequiredAt)} />
          <Row label="Origen de la revisión" value={fmtStr(detail.reviewSource)} />
          <Row label="Motivo" value={fmtStr(detail.reviewReason)} />
        </>
      ) : (
        <p className="smap-detail-empty">Sin evidencia adicional de revisión</p>
      )}
      <p className="smap-advanced-note">QA avanzado (street-name, service-edge, clasificación combinada, candidato alternativo) — pendiente de consolidación</p>
    </div>
  );
}

/**
 * "Candidato seleccionado" — SOLO una selección visual del usuario sobre
 * la capa Red urbana, nunca una recomendación automática ni un cambio de
 * asociación. `distanceM` se deriva en el momento (turf), nunca persiste.
 */
function CandidateSection({
  candidate,
  onClear,
}: {
  candidate: { edge: UrbanNetworkEdge; distanceM: number | null };
  onClear: () => void;
}): JSX.Element {
  return (
    <div className="smap-detail-section">
      <div className="smap-detail-header" style={{ marginBottom: '6px' }}>
        <p className="smap-detail-section-title" style={{ margin: 0 }}>
          Candidato seleccionado
        </p>
        <button type="button" className="smap-detail-close" onClick={onClear} aria-label="Limpiar candidato">
          ✕
        </button>
      </div>
      <p className="smap-detail-empty">Selección visual del usuario — no es una recomendación automática.</p>
      <Row label="Canonical edge" value={candidate.edge.id} />
      <Row label="Distancia" value={candidate.distanceM !== null ? `${fmtNum(candidate.distanceM, 1)} m` : SIN_DATO} />
      <Row label="Referencia" value={fmtStr(candidate.edge.sourceReference)} />
      <Row label="Route ref" value={fmtStr(candidate.edge.routeRef)} />
      <Row label="Clase de vía" value={fmtStr(candidate.edge.roadClass)} />
    </div>
  );
}

type PanelMode = 'closed' | 'loading' | 'error' | 'detail';

function resolveMode(detail: SystemMapStopDetail | null, loading: boolean, error: string | null): PanelMode {
  if (loading) return 'loading';
  if (error) return 'error';
  if (detail) return 'detail';
  return 'closed';
}

function DetailContent({
  detail,
  candidate,
  editing,
  sessionOverride,
  onClose,
  onClearCandidate,
  onToggleEdit,
  onCancelEdit,
  onConfirmInSession,
}: {
  detail: SystemMapStopDetail;
  candidate: { edge: UrbanNetworkEdge; distanceM: number | null } | null;
  editing: boolean;
  sessionOverride: StopOverride | undefined;
  onClose: () => void;
  onClearCandidate: () => void;
  onToggleEdit: () => void;
  onCancelEdit: () => void;
  onConfirmInSession: () => void;
}): JSX.Element {
  return (
    <div className="smap-detail-inner">
      <div className="smap-detail-header">
        <div>
          <p className="smap-detail-title">{detail.name ?? `Parada ${detail.municipalStopId ?? detail.id}`}</p>
          <p className="smap-detail-sub">{statusLabel(detail.status)}</p>
        </div>
        <button type="button" className="smap-detail-close" onClick={onClose} aria-label="Cerrar">
          ✕
        </button>
      </div>
      {sessionOverride && <SessionOverrideBadge override={sessionOverride} />}
      <IdentificationSection detail={detail} />
      <AssociationSection detail={detail} editing={editing} onToggleEdit={onToggleEdit} />
      {candidate && <CandidateSection candidate={candidate} onClear={onClearCandidate} />}
      {editing && <EditModeBlock hasCandidate={candidate !== null} onCancel={onCancelEdit} />}
      <QaSection detail={detail} />
      <div className="smap-detail-section">
        <button type="button" className="smap-reset-btn" onClick={onConfirmInSession}>
          {sessionOverride ? 'Re-confirmar actual (sesión)' : 'Confirmar actual (sesión)'}
        </button>
        <p className="smap-persistence-note">Simula una confirmación visual solo en esta sesión — no persistida.</p>
      </div>
    </div>
  );
}

export function SystemMapPanel({
  detail,
  loading,
  error,
  candidate,
  editing,
  sessionOverride,
  onClose,
  onClearCandidate,
  onToggleEdit,
  onCancelEdit,
  onConfirmInSession,
}: {
  detail: SystemMapStopDetail | null;
  loading: boolean;
  error: string | null;
  candidate: { edge: UrbanNetworkEdge; distanceM: number | null } | null;
  editing: boolean;
  sessionOverride: StopOverride | undefined;
  onClose: () => void;
  onClearCandidate: () => void;
  onToggleEdit: () => void;
  onCancelEdit: () => void;
  onConfirmInSession: () => void;
}): JSX.Element {
  const mode = resolveMode(detail, loading, error);
  return (
    <div className={`smap-detail-panel${mode !== 'closed' ? ' smap-detail-panel--open' : ''}`}>
      {mode === 'loading' && <div className="smap-detail-loading">Cargando detalle…</div>}
      {mode === 'error' && <div className="smap-detail-error">{error}</div>}
      {mode === 'detail' && detail && (
        <DetailContent
          detail={detail}
          candidate={candidate}
          editing={editing}
          sessionOverride={sessionOverride}
          onClose={onClose}
          onClearCandidate={onClearCandidate}
          onToggleEdit={onToggleEdit}
          onCancelEdit={onCancelEdit}
          onConfirmInSession={onConfirmInSession}
        />
      )}
    </div>
  );
}
